import type {
  AlertSeverity,
  AlertStatus,
  ApplianceStatus,
  TenantPlan,
  TenantStatus,
} from '@minisoc/contracts';

/**
 * Correspondance valeur métier → classes utilitaires.
 *
 * Centralisé ici pour qu'une alerte critique ait exactement la même couleur
 * dans le backoffice et dans le dashboard client. Les composants ne codent
 * jamais une couleur de sévérité en dur.
 */

export interface ToneClasses {
  /** Fond + texte + bordure, pour les badges. */
  readonly badge: string;
  /** Texte seul, pour les compteurs et les liens. */
  readonly text: string;
  /** Fond plein, pour les pastilles et les barres. */
  readonly solid: string;
}

export const SEVERITY_TONE: Record<AlertSeverity, ToneClasses> = {
  CRITICAL: {
    badge: 'bg-critical/12 text-critical ring-1 ring-critical/30',
    text: 'text-critical',
    solid: 'bg-critical',
  },
  HIGH: {
    badge: 'bg-high/12 text-high ring-1 ring-high/30',
    text: 'text-high',
    solid: 'bg-high',
  },
  MEDIUM: {
    badge: 'bg-medium/12 text-medium ring-1 ring-medium/30',
    text: 'text-medium',
    solid: 'bg-medium',
  },
  LOW: {
    badge: 'bg-low/12 text-low ring-1 ring-low/30',
    text: 'text-low',
    solid: 'bg-low',
  },
};

export const ALERT_STATUS_TONE: Record<AlertStatus, ToneClasses> = {
  OPEN: {
    badge: 'bg-critical/12 text-critical ring-1 ring-critical/30',
    text: 'text-critical',
    solid: 'bg-critical',
  },
  IN_PROGRESS: {
    badge: 'bg-high/12 text-high ring-1 ring-high/30',
    text: 'text-high',
    solid: 'bg-high',
  },
  RESOLVED: {
    badge: 'bg-success/12 text-success ring-1 ring-success/30',
    text: 'text-success',
    solid: 'bg-success',
  },
};

export const APPLIANCE_STATUS_TONE: Record<ApplianceStatus, ToneClasses> = {
  ONLINE: {
    badge: 'bg-success/12 text-success ring-1 ring-success/30',
    text: 'text-success',
    solid: 'bg-success',
  },
  OFFLINE: {
    badge: 'bg-critical/12 text-critical ring-1 ring-critical/30',
    text: 'text-critical',
    solid: 'bg-critical',
  },
  MAINTENANCE: {
    badge: 'bg-high/12 text-high ring-1 ring-high/30',
    text: 'text-high',
    solid: 'bg-high',
  },
  PROVISIONING: {
    badge: 'bg-electric/12 text-electric ring-1 ring-electric/30',
    text: 'text-electric',
    solid: 'bg-electric',
  },
};

export const TENANT_PLAN_TONE: Record<TenantPlan, ToneClasses> = {
  // Progression gris → violet → bleu de marque : ESSENTIEL et ENTREPRISE ne
  // peuvent plus partager le bleu depuis que celui-ci est l'accent principal.
  ESSENTIEL: {
    badge: 'bg-low/12 text-low ring-1 ring-low/30',
    text: 'text-low',
    solid: 'bg-low',
  },
  PRO: {
    badge: 'bg-violet/12 text-violet ring-1 ring-violet/30',
    text: 'text-violet',
    solid: 'bg-violet',
  },
  ENTREPRISE: {
    badge: 'bg-brand-500/12 text-brand-400 ring-1 ring-brand-500/30',
    text: 'text-brand-400',
    solid: 'bg-brand-500',
  },
};

export const TENANT_STATUS_TONE: Record<TenantStatus, ToneClasses> = {
  ACTIVE: {
    badge: 'bg-success/12 text-success ring-1 ring-success/30',
    text: 'text-success',
    solid: 'bg-success',
  },
  ONBOARDING: {
    badge: 'bg-electric/12 text-electric ring-1 ring-electric/30',
    text: 'text-electric',
    solid: 'bg-electric',
  },
  SUSPENDED: {
    badge: 'bg-low/12 text-low ring-1 ring-low/30',
    text: 'text-low',
    solid: 'bg-low',
  },
};

/** Valeurs hexadécimales, pour les librairies de graphes qui n'acceptent pas de classes. */
export const CHART_COLORS = {
  critical: '#ff3b6b',
  high: '#ffb020',
  medium: '#3d7bff',
  low: '#7c8db5',
  success: '#2ee6a8',
  brand: '#3d7bff',
  violet: '#7b5cff',
  grid: '#1e2a45',
  axis: '#5d6d8f',
} as const;
